import { filter, reject } from 'lodash';

import { NOT_A_VACANT_RENTALOBJECT } from '@/config/constants';
import axios from '@/store/config';

export default {
  // state
  state: {
    publishedVacantApartments: {},
    publishedVacantParkings: {},
    publishedVacantPremises: {},
    publishedVacantStudentApartment: {},
    publishedVacantCultivationplots: {},
    singleFetchedVacantObjects: [],
    latestSearchResult: [],
  },
  // getters
  getters: {
    publishedVacantApartments: (state) => {
      return state.publishedVacantApartments;
    },
    publishedVacantParkings: (state) => {
      return state.publishedVacantParkings;
    },
    publishedVacantPremises: (state) => {
      return state.publishedVacantPremises;
    },
    publishedVacantStudentApartment: (state) => {
      return state.publishedVacantStudentApartment;
    },
    publishedVacantCultivationplots: (state) => {
      return state.publishedVacantCultivationplots;
    },

    /**
     * Leta i samlingen efter ledigt objekt på rentalObjectCode
     */
    getVacantObjectByRentalObjectCode: (state) => (rentalObjectCode) => {
      return state.singleFetchedVacantObjects.find((o) => o.rentalObjectCode == rentalObjectCode);
    },

    /**
     * Hämta nästa objekt i listan från senaste sökningen.
     *
     * Om det inte finns returneras false;
     */
    getNextRentalObjectFromSearchResult: (state) => (rentalObjectCode) => {
      if (state.latestSearchResult.length == 0) return false;

      const matchingIndex = state.latestSearchResult.findIndex(
        (o) => o.rentalObjectCode == rentalObjectCode,
      );

      if (matchingIndex + 1 > state.latestSearchResult.length) return false;

      return state.latestSearchResult[matchingIndex + 1];
    },

    /**
     * Hämta förra objektet i listan från senaste sökningen.
     *
     * Om det inte finns returneras false;
     */
    getPreviousRentalObjectFromSearchResult: (state) => (rentalObjectCode) => {
      if (state.latestSearchResult.length == 0) return false;

      const matchingIndex = state.latestSearchResult.findIndex(
        (o) => o.rentalObjectCode == rentalObjectCode,
      );

      if (matchingIndex == 0) return false;

      return state.latestSearchResult[matchingIndex - 1];
    },
  },

  // actions
  actions: {
    /**
     * Hämta alla lediga bostäder och studentbostäder
     */
    getPublishedVacantApartments({ commit }) {
      return axios
        .get('/publishedrentalobjects/apartments')
        .then((response) => {
          commit('setPublishedVacantApartments', response.data);
        })
        .catch((err) => {
          if (axios.isCancel(err) || err.code === 'ECONNABORTED') {
            // Requesten blev avbruten
            console.warn('Request aborted:', err.message);
            return;
          }
          console.error('Axios error:', err);
          throw err; // låt riktiga fel bubbla vidare
        });
    },

    /**
     * Hämta alla lediga fordonsplatser
     */
    getPublishedVacantParkings({ commit }) {
      return axios.get('/publishedrentalobjects/parkings').then((response) => {
        commit('setPublishedVacantParkings', response.data);
      });
    },

    /**
     * Hämta alla lediga förråd
     */
    getPublishedVacantPremises({ commit }) {
      return axios.get('/publishedrentalobjects/premises').then((response) => {
        commit('setPublishedVacantPremises', response.data);
      });
    },

    /**
     * Hämta alla lediga odlingslotter
     */
    getPublishedVacantCultivationplots({ commit }) {
      return axios.get('/publishedrentalobjects/cultivationplots').then((response) => {
        commit('setPublishedVacantCultivationplots', response.data);
      });
    },

    /**
     * Hämta enskilt ledigt rentalobjekt på rentalObjectCode
     */
    fetchVacantObjectByRentalObjectCode({ commit, dispatch }, rentalObjectCode) {
      return new Promise((resolve, reject) => {
        if (!rentalObjectCode || rentalObjectCode == '' || rentalObjectCode == undefined) {
          return reject(NOT_A_VACANT_RENTALOBJECT);
        }

        // NOT_A_VACANT_RENTALOBJECT
        axios.get('/rentalobjects/' + rentalObjectCode).then((response) => {
          commit('pushRentalObjects', response.data);

          let path = '';

          if (response.data.objectType == 'otherRentalObject')
            path = 'cultivationplots/' + rentalObjectCode;
          else path = response.data.objectType + 's/' + rentalObjectCode;

          // endpointsen är parkings, premises, apartments. Alltså objecttype + 's'
          axios
            .get('/publishedrentalobjects/' + path)
            .then((response) => {
              commit('pushSingleVacantObject', response.data);
              resolve(response.data);
            })
            .catch((e) => {
              if (e.response.status === 404) reject(NOT_A_VACANT_RENTALOBJECT);
            });
        });
      });
    },

    /**
     * Sätt det senaste sökresultat.
     * Används på "Nästa" knapp på en detaljsida
     */
    setLatestSearchResult({ commit }, searchResult) {
      commit('setLatestSearchResult', searchResult);
    },
  },

  // mutations
  mutations: {
    setPublishedVacantApartments(state, publishedVacantApartments) {
      state.publishedVacantApartments = reject(publishedVacantApartments, function (o) {
        return o.rentalObjectType === 'LGH20';
      });
      state.publishedVacantStudentApartment = filter(publishedVacantApartments, function (o) {
        return o.rentalObjectType === 'LGH20';
      });
    },
    setPublishedVacantParkings(state, publishedVacantParkings) {
      state.publishedVacantParkings = publishedVacantParkings;
    },
    setPublishedVacantPremises(state, publishedVacantPremises) {
      state.publishedVacantPremises = publishedVacantPremises;
    },
    setPublishedVacantCultivationplots(state, publishedVacantCultivationplots) {
      state.publishedVacantCultivationplots = publishedVacantCultivationplots;
    },

    /**
     * Lägg till enskilt hämtat rentalobjekt i samlingen.
     * Ersätter eventuell tidigare version
     */
    pushSingleVacantObject(state, data) {
      state.singleFetchedVacantObjects = [
        ...state.singleFetchedVacantObjects.filter(
          (n) => n.rentalObjectCode != data.rentalObjectCode,
        ),
        data,
      ];
    },

    /**
     * Senaste sökresultatet.
     */
    setLatestSearchResult(state, data) {
      state.latestSearchResult = data;
    },
  },
};
